// src/lib/sentry.ts
// Thin wrapper around @sentry/nextjs for error reporting.
//
// In development we log to the console instead of firing off to Sentry —
// this keeps local noise out of the production project and avoids the need
// for a DSN during development.

import * as Sentry from '@sentry/nextjs'

/**
 * Capture an unexpected error and ship it to Sentry in production.
 * In development, logs to the console instead.
 */
export function captureError(error: unknown, context?: Record<string, unknown>): void {
  if (process.env.NODE_ENV === 'development') {
    console.error('[dev] error:', error, context)
    return
  }
  Sentry.captureException(error, { extra: context })
}

/**
 * Capture a non-error message (useful for warnings and informational events).
 */
export function captureMessage(
  message: string,
  level: 'info' | 'warning' | 'error' = 'info',
): void {
  Sentry.captureMessage(message, level)
}

/**
 * Attach user context to all subsequent Sentry events on the current scope.
 * Pass `null` to clear (e.g. on sign-out).
 *
 * We deliberately attach ONLY the opaque user id — never email or any other
 * PII. Shipping email to a third-party error tracker on every authenticated
 * error is a privacy leak (and a GDPR-K concern given many of our users are
 * minors). The id is enough to correlate errors to a user internally. (#818)
 */
export function setUser(user: { id: string } | null): void {
  if (user) {
    Sentry.setUser({ id: user.id })
  } else {
    Sentry.setUser(null)
  }
}
